import React, { useState } from 'react';
import { MapPin, Phone, Mail, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const regionMetadata = {
  middleEast: [
    { flag: '🇦🇪', phone: '80050800', email: 'info@baicuae.com' },
    { flag: '🇸🇦', phone: '+966 800 2242', email: 'info@baicsa.com' },
    { flag: '🇶🇦', phone: '+974 800 2242', email: 'info@baicqatar.com' },
  ],
  asia: [
    { flag: '🇨🇳', phone: '+86 10 8888 2242', email: 'info@baic.com.cn' },
    { flag: '🇹🇭', phone: '+66 2 888 2242', email: 'info@baicthailand.com' },
  ],
  africa: [
    { flag: '🇿🇦', phone: '+27 11 888 2242', email: 'info@baicsa.co.za' },
    { flag: '🇪🇬', phone: '+20 2 888 2242', email: 'info@baicegypt.com' },
  ],
  europe: [
    { flag: '🇩🇪', phone: '+49 30 888 2242', email: 'info@baiceurope.de' },
  ],
  americas: [
    { flag: '🇲🇽', phone: '+52 55 888 2242', email: 'info@baicmexico.com' },
  ],
};

const Worldwide = () => {
  const { language } = useLanguage();
  const t = translations[language].worldwide;
  const [selectedRegion, setSelectedRegion] = useState('middleEast');

  const regionKeys = ['middleEast', 'asia', 'africa', 'europe', 'americas'];

  return (
    <div className="min-h-screen bg-white pt-[120px]">
      {/* Hero Section */}
      <div className="relative h-96 bg-gradient-to-r from-gray-900 to-gray-700">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full flex items-center justify-center text-center">
          <div>
            <h1 className="text-6xl font-bold text-white mb-4">{t.title}</h1>
            <p className="text-xl text-white/90">{t.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Stats Banner */}
      <div className="bg-red-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-5xl font-bold mb-2">80+</div>
              <div className="text-red-100">{t.stats.countries}</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-red-100">{t.stats.dealerships}</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">1000+</div>
              <div className="text-red-100">{t.stats.serviceCenters}</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">5M+</div>
              <div className="text-red-100">{t.stats.customers}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Locations */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Region Selector */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {regionKeys.map((regionKey) => (
            <button
              key={regionKey}
              onClick={() => setSelectedRegion(regionKey)}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${selectedRegion === regionKey
                ? 'bg-red-600 text-white shadow-lg scale-105'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {t.regions[regionKey]}
            </button>
          ))}
        </div>

        {/* Country Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.locations[selectedRegion]?.map((location, index) => {
            const meta = regionMetadata[selectedRegion][index];
            return (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 text-white">
                  <div className="text-5xl mb-3">{meta.flag}</div>
                  <h3 className="text-2xl font-bold">{location.country}</h3>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-500 mb-3">{t.availableIn}</h4>
                    <div className="flex flex-wrap gap-2">
                      {location.cities.map((city, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-red-50 text-red-600 rounded-full text-sm font-medium"
                        >
                          {city}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <a
                      href={`tel:${meta.phone}`}
                      className="flex items-center gap-3 text-gray-700 hover:text-red-600 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      <span className="text-sm">{meta.phone}</span>
                    </a>
                    <a
                      href={`mailto:${meta.email}`}
                      className="flex items-center gap-3 text-gray-700 hover:text-red-600 transition-colors"
                    >
                      <Mail className="h-4 w-4" />
                      <span className="text-sm">{meta.email}</span>
                    </a>
                  </div>

                  <button className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-red-600 hover:text-white rounded-xl font-semibold transition-all group">
                    <span>{t.findDealer}</span>
                    <ChevronRight className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MapPin className="h-16 w-16 text-red-600 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t.cantFind.title}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t.cantFind.desc}
          </p>
          <button className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-full font-semibold text-lg transition-all transform hover:scale-105">
            {t.cantFind.btn}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Worldwide;